/**
 * 记忆管理页面（增强版）
 * 支持用户档案、风格分析、记忆设置等功能
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import Header from '@/components/layout/Header';
import Sidebar from '@/components/layout/Sidebar';
import StatusBar from '@/components/layout/StatusBar';
import MobileMenu from '@/components/common/MobileMenu';
import SettingsModal from '@/components/modals/SettingsModal';
import {
  Database,
  User,
  Palette,
  Sparkles,
  Clock,
  Save,
  Trash2,
  Plus,
  Edit,
  BarChart3,
  FileText,
  Wand2
} from 'lucide-react';
import api from '@/services/api';

type UserProfile = {
  user_id: string;
  display_name?: string;
  preferences?: {
    theme?: string;
    language?: string;
    notification_enabled?: boolean;
  };
  writing_style?: {
    tone?: string;
    perspective?: string;
    pacing?: string;
  };
  demographics?: {
    age_range?: string;
    interests?: string[];
  };
  created_at?: string;
  updated_at?: string;
};

type StyleAnalysis = {
  style_profile: {
    tone: string;
    perspective: string;
    vocabulary_level: string;
    sentence_structure: string;
    dialogue_style?: string;
  };
  confidence: number;
  sample_count: number;
  last_updated: string;
};

type MemorySettings = {
  user_enabled: boolean;
  project_enabled: boolean;
  retention_days: number;
  max_context_items: number;
  effective_enabled: boolean;
  updated_at: string;
};

type StyleExample = {
  id: string;
  name: string;
  description: string;
  style_profile: any;
};

type MemorySnapshot = {
  snapshot_id: string;
  created_at: string;
  description: string;
  label?: string;
  item_count: number;
};

type PageNotice = {
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
};

const defaultProfile = (userId: string): UserProfile => ({
  user_id: userId,
  display_name: '默认用户',
  preferences: {
    theme: 'light',
    language: 'zh-CN',
    notification_enabled: true,
  },
  writing_style: {
    tone: '',
    perspective: '',
    pacing: '',
  },
  demographics: {
    interests: [],
  },
});

const defaultSettings: MemorySettings = {
  user_enabled: true,
  project_enabled: true,
  retention_days: 30,
  max_context_items: 20,
  effective_enabled: true,
  updated_at: '',
};

function unwrapData<T>(response: any, fallback: T): T {
  return (response?.data ?? response ?? fallback) as T;
}

function normalizeStyleAnalysis(payload: any): StyleAnalysis {
  const source = payload?.analysis || payload || {};
  const features = source.style_profile || source.features || {};
  return {
    style_profile: {
      tone: features.tone || 'neutral',
      perspective: features.perspective || 'third_person_limited',
      vocabulary_level: features.vocabulary_level || 'standard',
      sentence_structure: features.sentence_structure || 'mixed',
      dialogue_style: features.dialogue_style,
    },
    confidence: source.confidence ?? (source.degraded ? 40 : 60),
    sample_count: source.sample_count ?? 1,
    last_updated: source.last_updated || new Date().toISOString(),
  };
}

function isSuccessResponse(response: any): boolean {
  return response?.success !== false;
}

export default function MemoryManagementPage() {
  const { sidebarOpen, sidebarCollapsed } = useUIStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'profile' | 'style' | 'settings' | 'snapshots'>('profile');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [styleAnalysis, setStyleAnalysis] = useState<StyleAnalysis | null>(null);
  const [memorySettings, setMemorySettings] = useState<MemorySettings | null>(null);
  const [styleExamples, setStyleExamples] = useState<StyleExample[]>([]);
  const [snapshots, setSnapshots] = useState<MemorySnapshot[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [styleInput, setStyleInput] = useState('');
  const [pageNotice, setPageNotice] = useState<PageNotice | null>(null);

  const currentUserId = user?.id || 'default_user';

  // 加载用户档案
  const loadUserProfile = useCallback(async () => {
    try {
      const response = await api.get(`/juben/user/profile/${currentUserId}`);
      const data = unwrapData<UserProfile | null>(response, null);
      setUserProfile(data || defaultProfile(currentUserId));
    } catch {
      setUserProfile(defaultProfile(currentUserId));
      setPageNotice({ type: 'warning', message: '用户档案服务暂不可用，已使用默认档案占位。' });
    }
  }, [currentUserId]);

  // 加载风格分析
  const loadStyleAnalysis = useCallback(async () => {
    try {
      const response = await api.get('/juben/user/style/examples');
      const data = unwrapData<{ examples?: StyleExample[] }>(response, { examples: [] });
      setStyleExamples(Array.isArray(data?.examples) ? data.examples : []);
    } catch {
      setStyleExamples([]);
      setPageNotice({ type: 'warning', message: '风格示例暂不可用，不影响手动风格分析。' });
    }
  }, []);

  // 分析用户风格
  const analyzeStyle = async (content: string) => {
    if (!content.trim()) {
      setPageNotice({ type: 'info', message: '请输入要分析的内容后再运行风格分析。' });
      return;
    }

    setIsAnalyzing(true);
    setPageNotice(null);
    try {
      const response = await api.post('/juben/user/style/analyze', {
        user_id: currentUserId,
        content,
        analysis_type: 'comprehensive'
      });

      setStyleAnalysis(normalizeStyleAnalysis(unwrapData(response, {})));
      setPageNotice({ type: 'success', message: '风格分析完成，可按需保存为风格配置。' });
    } catch {
      setStyleAnalysis(normalizeStyleAnalysis({ degraded: true }));
      setPageNotice({ type: 'warning', message: '风格分析服务暂不可用，已展示可测试的降级分析结果。' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 保存用户档案
  const saveUserProfile = async () => {
    if (!userProfile) return;

    try {
      const response = await api.post('/juben/user/profile', {
        ...userProfile,
        user_id: currentUserId
      });

      if (isSuccessResponse(response)) {
        setPageNotice({ type: 'success', message: '档案保存成功。' });
      } else {
        setPageNotice({ type: 'warning', message: '档案暂未保存到后端，当前页面内容已保留。' });
      }
    } catch {
      setPageNotice({ type: 'warning', message: '档案服务暂不可用，当前页面内容已保留为本地显示状态。' });
    }
  };

  // 保存风格配置
  const saveStyleProfile = async (styleProfile: any) => {
    try {
      const response = await api.post('/juben/user/style/save', {
        user_id: currentUserId,
        style_profile: styleProfile
      });

      if (isSuccessResponse(response)) {
        setPageNotice({ type: 'success', message: '风格配置保存成功。' });
      } else {
        setPageNotice({ type: 'warning', message: '风格配置暂未保存到后端，可继续在本页查看分析结果。' });
      }
    } catch {
      setPageNotice({ type: 'warning', message: '风格配置服务暂不可用，可继续在本页查看分析结果。' });
    }
  };

  // 加载记忆快照
  const loadSnapshots = useCallback(async () => {
    try {
      const response = await api.get('/juben/user/memory/snapshots', { user_id: currentUserId, session_id: 'default_session' });
      const data = unwrapData<any>(response, []);
      const list = Array.isArray(data) ? data : data?.snapshots || [];
      setSnapshots(list.map((item: any) => ({
        snapshot_id: item.snapshot_id,
        created_at: item.created_at,
        description: item.description || item.label || '记忆快照',
        label: item.label,
        item_count: item.item_count ?? item.memory_count ?? 0,
      })));
    } catch {
      setSnapshots([]);
      setPageNotice({ type: 'warning', message: '记忆快照服务暂不可用，已显示空快照状态。' });
    }
  }, [currentUserId]);

  const loadMemorySettings = useCallback(async () => {
    try {
      const response = await api.get('/juben/user/memory/settings', { user_id: currentUserId });
      setMemorySettings({ ...defaultSettings, ...unwrapData<Partial<MemorySettings>>(response, {}) });
    } catch {
      setMemorySettings(defaultSettings);
      setPageNotice({ type: 'warning', message: '记忆设置服务暂不可用，已使用默认设置占位。' });
    }
  }, [currentUserId]);

  const updateMemorySetting = async (patch: Partial<MemorySettings>) => {
    const next = { ...(memorySettings || defaultSettings), ...patch };
    setMemorySettings(next);
    try {
      const response = await api.post('/juben/user/memory/settings', {
        user_id: currentUserId,
        user_enabled: next.user_enabled,
        project_enabled: next.project_enabled,
      });
      setMemorySettings({ ...next, ...unwrapData<Partial<MemorySettings>>(response, {}) });
      setPageNotice({ type: 'success', message: '记忆设置已保存。' });
    } catch {
      setPageNotice({ type: 'warning', message: '记忆设置暂不可保存，已保留本地显示状态。' });
    }
  };

  // 创建快照
  const createSnapshot = async (description: string) => {
    try {
      const response = await api.post('/juben/user/memory/snapshots', {
        user_id: currentUserId,
        session_id: 'default_session',
        description
      });

      if (isSuccessResponse(response)) {
        setPageNotice({ type: 'success', message: '快照创建成功。' });
        loadSnapshots();
      } else {
        setPageNotice({ type: 'warning', message: '快照暂未写入后端，记忆快照服务可能尚未接通。' });
      }
    } catch {
      setPageNotice({ type: 'warning', message: '快照服务暂不可用，无法创建真实快照。' });
    }
  };

  // 恢复快照
  const restoreSnapshot = async (snapshotId: string) => {
    if (!confirm('确定要恢复到此快照吗？当前记忆状态将被覆盖。')) return;

    try {
      const response = await api.post(`/juben/user/memory/snapshots/${snapshotId}/restore`, {});
      if (isSuccessResponse(response)) {
        setPageNotice({ type: 'success', message: '快照恢复成功。' });
      } else {
        setPageNotice({ type: 'warning', message: '快照恢复未写入后端，记忆服务可能尚未接通。' });
      }
    } catch {
      setPageNotice({ type: 'warning', message: '快照服务暂不可用，无法恢复真实快照。' });
    }
  };

  useEffect(() => {
    loadUserProfile();
    loadStyleAnalysis();
    loadMemorySettings();
    loadSnapshots();
  }, [loadMemorySettings, loadSnapshots, loadStyleAnalysis, loadUserProfile]);

  return (
    <div className="flex h-screen bg-white">
      <MobileMenu />
      <Sidebar />

      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${
          !sidebarOpen ? 'ml-0' : sidebarCollapsed ? 'ml-16' : 'ml-80'
        }`}
      >
        <Header />
        <StatusBar />

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* 页面头部 */}
          <div className="border-b border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">记忆管理</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    管理用户档案、写作风格和记忆设置
                  </p>
                </div>
              </div>

              {/* 标签页 */}
              <div className="flex items-center gap-2 mt-6">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'profile'
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <User className="w-4 h-4 mr-2" />
                  用户档案
                </button>
                <button
                  onClick={() => setActiveTab('style')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'style'
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Palette className="w-4 h-4 mr-2" />
                  风格分析
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  记忆设置
                </button>
                <button
                  onClick={() => setActiveTab('snapshots')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'snapshots'
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  记忆快照
                </button>
              </div>
            </div>
          </div>

          {/* 主内容区 */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto">
              {pageNotice && (
                <div
                  className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                    pageNotice.type === 'success'
                      ? 'border-green-200 bg-green-50 text-green-800'
                      : pageNotice.type === 'error'
                        ? 'border-red-200 bg-red-50 text-red-800'
                        : pageNotice.type === 'warning'
                          ? 'border-orange-200 bg-orange-50 text-orange-800'
                          : 'border-blue-200 bg-blue-50 text-blue-800'
                  }`}
                >
                  {pageNotice.message}
                </div>
              )}

              {activeTab === 'profile' && (
                userProfile ? (
                  <div className="space-y-6">
                  {/* 用户基本信息 */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-semibold text-gray-900">用户档案</h2>
                      <button
                        onClick={saveUserProfile}
                        className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                      >
                        <Save className="w-4 h-4" />
                        保存
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">显示名称</label>
                        <input
                          type="text"
                          value={userProfile.display_name || ''}
                          onChange={(e) => setUserProfile({ ...userProfile, display_name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-black text-sm"
                          placeholder="输入显示名称"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">用户ID</label>
                        <input
                          type="text"
                          value={userProfile.user_id}
                          disabled
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm"
                        />
                      </div>
                    </div>

                    {/* 写作风格偏好 */}
                    <div className="mt-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">写作风格偏好</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">基调</label>
                          <select
                            value={userProfile.writing_style?.tone || ''}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              writing_style: { ...userProfile.writing_style, tone: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                          >
                            <option value="">选择基调</option>
                            <option value="formal">正式</option>
                            <option value="casual">随意</option>
                            <option value="dramatic">戏剧化</option>
                            <option value="humorous">幽默</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">视角</label>
                          <select
                            value={userProfile.writing_style?.perspective || ''}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              writing_style: { ...userProfile.writing_style, perspective: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                          >
                            <option value="">选择视角</option>
                            <option value="first_person">第一人称</option>
                            <option value="third_person_limited">第三人称限制</option>
                            <option value="third_person_omniscient">第三人称全知</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">节奏</label>
                          <select
                            value={userProfile.writing_style?.pacing || ''}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              writing_style: { ...userProfile.writing_style, pacing: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                          >
                            <option value="">选择节奏</option>
                            <option value="fast">快节奏</option>
                            <option value="medium">中等节奏</option>
                            <option value="slow">慢节奏</option>
                            <option value="variable">多变节奏</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* 兴趣标签 */}
                    <div className="mt-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">兴趣标签</h3>
                      <div className="flex flex-wrap gap-2">
                        {userProfile.demographics?.interests?.map((interest, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {interest}
                          </span>
                        )) || <span className="text-sm text-gray-400">无</span>}
                      </div>
                    </div>
                  </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
                    正在加载用户档案；如果存储不可用，将使用默认档案占位。
                  </div>
                )
              )}

              {activeTab === 'style' && (
                <div className="space-y-6">
                  {/* 风格分析工具 */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">风格分析</h2>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">分析内容</label>
                        <textarea
                          value={styleInput}
                          onChange={(e) => setStyleInput(e.target.value)}
                          placeholder="输入要分析风格的内容..."
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black text-sm resize-none"
                        />
                      </div>

                      <button
                        onClick={() => analyzeStyle(styleInput)}
                        disabled={isAnalyzing}
                        className="w-full px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {isAnalyzing ? (
                          <>
                            分析中...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            分析风格
                          </>
                        )}
                      </button>
                    </div>

                    {/* 分析结果 */}
                    {styleAnalysis && (
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="text-sm font-medium text-blue-900 mb-3">分析结果</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-blue-700 mb-1">基调</p>
                            <p className="text-sm font-medium text-blue-900">{styleAnalysis.style_profile.tone}</p>
                          </div>

                          <div>
                            <p className="text-xs text-blue-700 mb-1">视角</p>
                            <p className="text-sm font-medium text-blue-900">{styleAnalysis.style_profile.perspective}</p>
                          </div>

                          <div>
                            <p className="text-xs text-blue-700 mb-1">词汇水平</p>
                            <p className="text-sm font-medium text-blue-900">{styleAnalysis.style_profile.vocabulary_level}</p>
                          </div>

                          <div>
                            <p className="text-xs text-blue-700 mb-1">句子结构</p>
                            <p className="text-sm font-medium text-blue-900">{styleAnalysis.style_profile.sentence_structure}</p>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <div className="flex items-center justify-between text-xs text-blue-700">
                            <span>置信度</span>
                            <span className="font-medium">{styleAnalysis.confidence?.toFixed(1)}%</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-blue-700 mt-1">
                            <span>样本数量</span>
                            <span className="font-medium">{styleAnalysis.sample_count}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-blue-700 mt-1">
                            <span>最后更新</span>
                            <span className="font-medium">
                              {styleAnalysis.last_updated ? new Date(styleAnalysis.last_updated).toLocaleString('zh-CN') : '-'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-blue-200 flex gap-2">
                          <button
                            onClick={() => saveStyleProfile(styleAnalysis.style_profile)}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            保存为风格配置
                          </button>
                          <button
                            onClick={() => setStyleAnalysis(null)}
                            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                          >
                            清除结果
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 风格示例 */}
                  {styleExamples.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">风格示例</h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {styleExamples.map((example) => (
                          <div
                            key={example.id}
                            className="p-4 border border-gray-200 rounded-lg hover:shadow-lg transition-all cursor-pointer"
                            onClick={() => {
                              if (confirm('确定要应用此风格配置吗？')) {
                                saveStyleProfile(example.style_profile);
                              }
                            }}
                          >
                            <h3 className="text-sm font-medium text-gray-900 mb-2">{example.name}</h3>
                            <p className="text-xs text-gray-600">{example.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">记忆设置</h2>
                        <p className="text-sm text-gray-500 mt-1">
                          当前页面先提供可测试的轻量设置；真实长期记忆依赖后续数据库/向量库接通。
                        </p>
                      </div>
                      <button
                        onClick={loadMemorySettings}
                        className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        刷新
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        onClick={() => updateMemorySetting({ user_enabled: !(memorySettings || defaultSettings).user_enabled })}
                        className={`p-4 rounded-xl border text-left transition-colors ${
                          (memorySettings || defaultSettings).user_enabled
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-sm font-semibold">用户记忆</div>
                        <div className="text-xs mt-2 opacity-80">
                          {(memorySettings || defaultSettings).user_enabled ? '已开启' : '已关闭'}
                        </div>
                      </button>

                      <button
                        onClick={() => updateMemorySetting({ project_enabled: !(memorySettings || defaultSettings).project_enabled })}
                        className={`p-4 rounded-xl border text-left transition-colors ${
                          (memorySettings || defaultSettings).project_enabled
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-sm font-semibold">项目记忆</div>
                        <div className="text-xs mt-2 opacity-80">
                          {(memorySettings || defaultSettings).project_enabled ? '已开启' : '已关闭'}
                        </div>
                      </button>

                      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <div className="text-sm font-semibold text-gray-900">生效状态</div>
                        <div className="text-xs text-gray-600 mt-2">
                          {(memorySettings || defaultSettings).effective_enabled ? '当前会注入记忆上下文' : '当前不注入记忆上下文'}
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          更新时间：{(memorySettings || defaultSettings).updated_at || '暂无'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'snapshots' && (
                <div className="space-y-6">
                  {/* 创建快照 */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">创建快照</h2>
                      <button
                        onClick={() => {
                          const description = prompt('请输入快照描述：', `快照 ${new Date().toLocaleString('zh-CN')}`);
                          if (description) {
                            createSnapshot(description);
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        创建快照
                      </button>
                    </div>

                    <p className="text-sm text-gray-500">
                      快照可以保存当前的记忆状态，方便后续恢复
                    </p>
                  </div>

                  {/* 快照列表 */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">快照列表</h2>

                    {snapshots.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>暂无快照</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {snapshots.map((snapshot) => (
                          <div
                            key={snapshot.snapshot_id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all"
                          >
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">{snapshot.description}</h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {snapshot.item_count} 个记忆项 · {new Date(snapshot.created_at).toLocaleString('zh-CN')}
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => restoreSnapshot(snapshot.snapshot_id)}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                              >
                                恢复
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('确定要删除此快照吗？')) {
                                    setSnapshots(prev => prev.filter(s => s.snapshot_id !== snapshot.snapshot_id));
                                  }
                                }}
                                className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SettingsModal />
    </div>
  );
}
